import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Barra, Bloque, Cifra, Pagina, Tabla } from '../componentes/Estructura';
import { Cargando, ConError, Vacio } from '../componentes/Estados';
import type { Tono } from '../componentes/Etiqueta';
import { DialogoMotivo } from '../componentes/DialogoMotivo';
import { laFechaHora, porcentaje } from '../componentes/formato';
import { useCargaReal } from '../dominio/carga';
import { ErrorApi } from '../dominio/clienteApi';
import { obtenerTasaVictoria } from '../dominio/dispatchApi';
import type { DiagnosticoTasaVictoria } from '../dominio/dispatchApi';
import {
  obtenerParametrosDispatch,
  publicarParametrosDispatch,
} from '../dominio/parametrosDispatchApi';
import type { ParametrosEfectivos } from '../dominio/parametrosDispatchApi';

/** Debajo de esta tasa, el conductor recibe ofertas y casi nunca gana — mismo umbral que usa `Panel.tsx`. */
const TASA_BAJA = 0.1;

/**
 * `02` §9.2bis: el DIAGNÓSTICO lo calcula el backend (`tasa-victoria.service.ts`); acá sólo se
 * traduce a castellano y a un tono, nunca se re-deriva de `medianaTasa`/`p10Tasa` a mano.
 */
const TEXTO_DIAGNOSTICO: Record<DiagnosticoTasaVictoria, { texto: string; tono: Tono }> = {
  SIN_MUESTRA: {
    texto: 'Nadie compitió en el período: no se puede decir nada todavía.',
    tono: 'neutro',
  },
  SIN_DEMANDA: {
    texto: 'Tasa baja para todos, sin dispersión: parece falta de demanda, no reparto desigual.',
    tono: 'atencion',
  },
  REPARTO_CONCENTRADO: {
    texto: 'La cola de abajo está muy por debajo de la mediana: el reparto se está concentrando.',
    tono: 'malo',
  },
  NORMAL: { texto: 'Sin señal de concentración en el período.', tono: 'bueno' },
};

/** El mensaje que ve el operador cuando la publicación falla. `ErrorApi` ya trae el motivo del backend. */
const mensajeDeFalla = (error: unknown): string =>
  error instanceof ErrorApi ? error.message : 'Ocurrió un error inesperado. Probá de nuevo.';

/**
 * Los SEIS parámetros editables. El backend publica un SNAPSHOT COMPLETO, no un parche, así que
 * los seis viajan en cada publicación aunque sólo cambie uno.
 *
 * ⚠️ **`frescuraPosicionMaxSeg` se agregó a esta lista y antes no estaba.** No es adorno: la
 * columna es obligatoria, así que la versión nueva lo lleva sí o sí. Dejarlo fuera del formulario
 * significaría que el operador firma con su nombre una versión que contiene un número que nunca
 * vio en pantalla — y estos números deciden el reparto de viajes.
 */
export type Editables = Pick<
  ParametrosEfectivos,
  | 'radioBusquedaM'
  | 'etaMaxSeg'
  | 'ventanaOfertaSeg'
  | 'nOfertaSimultanea'
  | 'maxRondasOferta'
  | 'frescuraPosicionMaxSeg'
>;

const CAMPOS: { clave: keyof Editables; etiqueta: string; unidad: string; ayuda: string }[] = [
  {
    clave: 'nOfertaSimultanea',
    etiqueta: 'A cuántos se ofrece a la vez',
    unidad: 'conductores',
    ayuda:
      'La perilla que reparte el trabajo. Más alto asigna más rápido y concentra los viajes en los mismos; más bajo reparte mejor y deja más viajes sin tomar. Se calibra mirando la tasa de victoria del panel, no el tiempo hasta la asignación.',
  },
  {
    clave: 'ventanaOfertaSeg',
    etiqueta: 'Tiempo para contestar la oferta',
    unidad: 'segundos',
    ayuda: 'Tiene que alcanzar para que alguien que está manejando atienda el aviso.',
  },
  {
    clave: 'radioBusquedaM',
    etiqueta: 'Radio de búsqueda',
    unidad: 'metros',
    ayuda:
      'Subirlo si quedan muchos viajes sin tomar; bajarlo si el filtro devuelve demasiados candidatos.',
  },
  {
    clave: 'etaMaxSeg',
    etiqueta: 'Tiempo máximo hasta el pasajero',
    unidad: 'segundos',
    ayuda:
      'Un conductor que tarde más que esto no recibe la oferta. Se calibra contra cuánto cancela el pasajero.',
  },
  {
    clave: 'maxRondasOferta',
    etiqueta: 'Rondas antes de rendirse',
    unidad: 'rondas',
    ayuda:
      'Agotadas las rondas, el viaje queda sin conductor y se le avisa al pasajero. Nunca se lo deja esperando.',
  },
  {
    clave: 'frescuraPosicionMaxSeg',
    etiqueta: 'Antigüedad máxima de la posición',
    unidad: 'segundos',
    ayuda:
      'Un conductor que no reporta posición hace más que esto sale del grupo al que se ofrece. Subirlo mete en el reparto a gente cuya ubicación ya no es confiable; bajarlo deja fuera a quien tiene señal intermitente.',
  },
];

/** Lo que gobierna el botón de publicar y el texto que lo acompaña. */
export interface EstadoPublicacion {
  /** Algún campo del formulario difiere de lo que el motor está usando ahora. */
  hayCambios: boolean;
  /** Los seis son enteros ≥ 1 — lo mismo que exige el backend. */
  valido: boolean;
  /** No hay fila vigente: el reparto lo gobierna el fallback declarado, no la tabla. */
  sinVersionVigente: boolean;
  /** Si el botón de publicar queda habilitado. */
  puedePublicar: boolean;
  /** Texto del botón. */
  textoBoton: string;
  /** Aclaración al lado del botón, o `null` si no hay nada que aclarar. */
  ayuda: string | null;
}

/**
 * Resuelve en un solo lugar si se puede publicar y qué se le dice al operador.
 *
 * 🔴 **`hayCambios` NO puede ser el único criterio, y el caso que lo rompe es TODA instalación
 * nueva.** La migración de Fase 1 crea `parametros_dispatch` VACÍA (ningún `INSERT`) y no hay
 * ningún seed en el repo ⇒ `vigentes()` no encuentra fila y devuelve `origen: 'FALLBACK'` ⇒ el
 * formulario arranca copiado de esos valores de arranque ⇒ los seis campos coinciden con
 * `efectivos` ⇒ con la regla vieja `hayCambios` daba `false`, el botón quedaba deshabilitado y
 * la pantalla decía "Sin cambios que guardar" justo debajo del banner rojo que pide publicar.
 * **El operador que está de acuerdo con los valores de arranque no podía publicarlos como
 * versión 1.** La única salida era ensuciar un número, publicar, y volver a publicar con el
 * valor bueno: dos versiones basura en la tabla que existe justamente para tener un historial
 * limpio, y firmadas con el nombre de alguien que no quería cambiar nada.
 *
 * Con el fallback activo, publicar SIEMPRE es un cambio de estado real aunque los seis números
 * sean idénticos: se pasa de "ninguna versión gobierna el reparto" a "existe la v1", y recién
 * desde ahí los viajes quedan atribuidos a una versión (R-10). Por eso la ausencia de versión
 * vigente alcanza por sí sola como motivo para publicar.
 *
 * ⚠️ **La validación NO se afloja:** `valido` y `publicando` siguen gobernando igual que antes.
 * Lo único que cambia es que `sinVersionVigente` se suma a `hayCambios` como motivo.
 *
 * Función PURA a propósito: la habilitación del botón que publica los números con los que se
 * reparte el trabajo se puede ejercitar sin montar React. Mismo criterio que `validarFormulario`
 * en `Tarifario.tsx`.
 */
export function calcularEstadoPublicacion(entrada: {
  valores: Editables;
  efectivos: ParametrosEfectivos;
  proximaVersion: number;
  publicando: boolean;
}): EstadoPublicacion {
  const { valores, efectivos, proximaVersion, publicando } = entrada;

  const hayCambios = CAMPOS.some(({ clave }) => valores[clave] !== efectivos[clave]);

  // Los seis son `@IsInt() @Min(1)` en el backend. Se comprueba acá para que el 400 no sea la
  // primera señal: el `<input type="number">` deja escribir `0`, vaciar el campo (que da `NaN`)
  // y pegar decimales.
  const valido = CAMPOS.every(
    ({ clave }) => Number.isInteger(valores[clave]) && valores[clave] >= 1,
  );

  const sinVersionVigente = efectivos.origen === 'FALLBACK';
  const puedePublicar = (hayCambios || sinVersionVigente) && valido && !publicando;

  // "Guardar" da por supuesto que hay algo escrito encima de una versión que ya existe. Sin
  // versión vigente eso es falso: no se guarda una edición, se publica la primera versión.
  const textoBoton = publicando
    ? 'Guardando…'
    : sinVersionVigente
      ? `Publicar como versión ${proximaVersion}`
      : `Guardar como versión ${proximaVersion}`;

  // 🔴 "Sin cambios que guardar." es VERDAD cuando hay una versión vigente idéntica, y MENTIRA
  // cuando no hay ninguna: ahí sí hay algo que hacer, y es exactamente lo que pide el banner
  // rojo de arriba. Los dos estados tienen que leerse distinto, o el operador no tiene cómo
  // saber qué va a pasar cuando apriete.
  const ayuda = !valido
    ? 'Los seis tienen que ser números enteros de 1 o más.'
    : hayCambios
      ? null
      : sinVersionVigente
        ? `No hay ninguna versión vigente: publicar deja estos valores de arranque guardados como la versión ${proximaVersion}, y desde ahí los viajes quedan atribuidos a ella.`
        : 'Sin cambios que guardar.';

  return { hayCambios, valido, sinVersionVigente, puedePublicar, textoBoton, ayuda };
}

export function Dispatch() {
  const [carga, recargar] = useCargaReal(() => obtenerParametrosDispatch(), []);

  const [borrador, setBorrador] = useState<Editables | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  // CA-05.9 / `02` §9.2bis — la tasa de victoria vive en OTRO endpoint y bajo otro prefijo
  // (`GET /dispatch/tasa-victoria`, sólo `JwtAuthGuard`). Ver la nota de `parametrosDispatchApi.ts`:
  // la anomalía es ese endpoint, no el de parámetros, y no se movió acá porque romper una ruta ya
  // publicada afecta a más de esta pantalla.
  const [tasaVictoria, recargarTasaVictoria] = useCargaReal(() => obtenerTasaVictoria(), []);

  if (carga.estado === 'cargando') {
    return (
      <Pagina rotulo="Solo administrador" titulo="Parámetros de dispatch">
        <div className="bloque">
          <Cargando filas={6} etiqueta="Cargando parámetros" />
        </div>
      </Pagina>
    );
  }

  if (carga.estado === 'error') {
    return (
      <Pagina rotulo="Solo administrador" titulo="Parámetros de dispatch">
        <div className="bloque">
          <ConError detalle={carga.mensaje} alReintentar={recargar} />
        </div>
      </Pagina>
    );
  }

  const { efectivos, historial } = carga.datos;

  /**
   * La versión que está VIGENTE en la tabla, o `undefined` si no hay ninguna.
   *
   * 🔴 **No es `historial[0]`.** Con el fallback activo el historial puede estar vacío, o traer
   * sólo versiones cerradas — y en los dos casos `historial[0]` mentiría diciendo que hay una
   * vigente. `vigenteHasta === null` es el mismo criterio que usa el backend para cerrarla, así
   * que las dos puntas dicen lo mismo.
   */
  const vigente = historial.find((version) => version.vigenteHasta === null);

  /**
   * Espeja `(anterior?.version ?? 0) + 1` del backend. Sin fila vigente la próxima es la 1,
   * NO `efectivos.version + 1`: cuando el origen es FALLBACK, `efectivos.version` es la del seed
   * del contrato y no la de ninguna fila — sumarle 1 anunciaría un número que el backend no va a
   * usar, y el operador leería "guardar como versión 2" para terminar creando la 1.
   */
  const proximaVersion = vigente ? vigente.version + 1 : 1;

  /**
   * El formulario arranca de `efectivos` y no de `vigente`, a propósito: `efectivos` es lo que el
   * motor está usando AHORA. Con el fallback activo no hay fila vigente de la cual partir, y
   * mostrar el formulario vacío o bloqueado dejaría al operador sin la única acción que arregla
   * la situación — publicar una versión.
   */
  const valores: Editables = borrador ?? {
    radioBusquedaM: efectivos.radioBusquedaM,
    etaMaxSeg: efectivos.etaMaxSeg,
    ventanaOfertaSeg: efectivos.ventanaOfertaSeg,
    nOfertaSimultanea: efectivos.nOfertaSimultanea,
    maxRondasOferta: efectivos.maxRondasOferta,
    frescuraPosicionMaxSeg: efectivos.frescuraPosicionMaxSeg,
  };

  const { hayCambios, sinVersionVigente, puedePublicar, textoBoton, ayuda } =
    calcularEstadoPublicacion({
      valores,
      efectivos,
      proximaVersion,
      publicando,
    });

  async function publicar(motivo: string): Promise<void> {
    setPublicando(true);
    setErrorAccion(null);
    try {
      await publicarParametrosDispatch({ ...valores, motivo });
      setConfirmando(false);
      setBorrador(null);
      recargar();
    } catch (error) {
      setConfirmando(false);
      setErrorAccion(mensajeDeFalla(error));
      // 🔴 El borrador NO se descarta cuando falla. Si la publicación se rechazó (un 409 porque
      // otro operador publicó en simultáneo, un 400), tirar lo que el operador acababa de escribir
      // lo obliga a rehacerlo de memoria. Se recarga el estado para que compare contra lo que
      // quedó vigente de verdad, y el borrador sigue en pantalla.
      recargar();
    } finally {
      setPublicando(false);
    }
  }

  return (
    <Pagina
      rotulo="Solo administrador"
      titulo="Parámetros de dispatch"
      ayuda="Cómo se reparten los viajes. Cada cambio crea una versión nueva con tu nombre y el motivo, así después se puede correlacionar un cambio de reparto con lo que pasó."
    >
      {errorAccion ? (
        <div className="bloque" style={{ marginBottom: 0 }}>
          <ConError titulo="No se pudo publicar la versión" detalle={errorAccion} />
        </div>
      ) : null}

      {/*
        🔴 `origen === 'FALLBACK'` significa que la TABLA NO ESTÁ GOBERNANDO EL REPARTO: no hay
        versión vigente, o la lectura falló. El motor sigue funcionando con los valores de arranque
        declarados, pero los viajes de ese rato quedan SIN `parametrosDispatchId`, o sea no son
        atribuibles a ninguna versión (R-10) y no se pueden correlacionar después. Sin este aviso,
        la pantalla mostraría los mismos números que en el caso sano.
      */}
      {efectivos.origen === 'FALLBACK' ? (
        <div className="bloque" style={{ marginBottom: 0 }}>
          <ConError
            titulo="El reparto no lo está gobernando la tabla"
            detalle="No hay ninguna versión vigente (o no se pudo leer), así que el sistema está usando los valores de arranque declarados. Los viajes que se repartan mientras tanto no quedan atribuidos a ninguna versión. Publicá una versión para corregirlo."
            alReintentar={recargar}
          />
        </div>
      ) : null}

      {/*
        CA-10.3 — versionados y con motivo obligatorio, y NUNCA constantes de
        código. El motivo no es burocracia: sin él, el histórico dice que el
        reparto cambió pero no por qué, y ahí se pierde justo la información que
        haría falta para entender qué pasó esa semana.
      */}
      <Bloque
        titulo={
          vigente
            ? `Versión ${vigente.version}, vigente`
            : 'Sin versión vigente — valores de arranque'
        }
        nota={
          vigente
            ? `Desde ${laFechaHora(vigente.vigenteDesde)} · la puso ${vigente.editadoPor ?? 'sin registrar'}`
            : 'Estos son los valores declarados de arranque, no una versión guardada.'
        }
      >
        <div
          className="rejilla"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}
        >
          {CAMPOS.map(({ clave, etiqueta, unidad, ayuda }) => (
            <label className="campo" key={clave}>
              <span className="campo__etiqueta rotulo">
                {etiqueta} <span className="apagado">· {unidad}</span>
              </span>
              <input
                className="entrada"
                type="number"
                min={1}
                step={1}
                value={Number.isNaN(valores[clave]) ? '' : valores[clave]}
                onChange={(evento) =>
                  setBorrador({ ...valores, [clave]: Number(evento.target.value) })
                }
              />
              <span className="campo__ayuda">{ayuda}</span>
              {valores[clave] !== efectivos[clave] ? (
                <span className="campo__ayuda" style={{ color: 'var(--ambar)' }}>
                  Ahora está en {efectivos[clave]}.
                </span>
              ) : null}
            </label>
          ))}
        </div>

        <div className="fila" style={{ marginTop: 6 }}>
          <button
            type="button"
            className="boton boton--principal"
            disabled={!puedePublicar}
            onClick={() => setConfirmando(true)}
          >
            {textoBoton}
          </button>
          <button
            type="button"
            className="boton"
            disabled={!hayCambios || publicando}
            onClick={() => setBorrador(null)}
          >
            Descartar cambios
          </button>
          {ayuda ? <span className="apagado">{ayuda}</span> : null}
        </div>
      </Bloque>

      <Bloque
        titulo="Historial"
        nota="Qué cambió, quién y por qué. Es lo que permite mirar un cambio de reparto contra lo que pasó después."
        sinRelleno
        acciones={
          <Link className="boton boton--chico" to="/">
            Ver el reparto
          </Link>
        }
      >
        {historial.length === 0 ? (
          <div className="bloque__cuerpo">
            <Vacio
              titulo="Todavía no hay ninguna versión guardada"
              detalle="La primera que publiques va a aparecer acá, con tu nombre y el motivo."
            />
          </div>
        ) : (
          <Tabla
            cabeceras={[
              'Versión',
              'Desde',
              { texto: 'N', num: true },
              { texto: 'Ventana', num: true },
              { texto: 'Radio', num: true },
              { texto: 'ETA máx.', num: true },
              { texto: 'Rondas', num: true },
              { texto: 'Frescura', num: true },
              'Quién',
              'Por qué',
            ]}
          >
            {historial.map((version) => (
              <tr
                key={version.id}
                data-estado={version.vigenteHasta === null ? 'bueno' : undefined}
              >
                <td>
                  {version.version}
                  {version.vigenteHasta === null ? <div className="apagado">vigente</div> : null}
                </td>
                <td className="tenue">{laFechaHora(version.vigenteDesde)}</td>
                <td className="tabla__num">{version.nOfertaSimultanea}</td>
                <td className="tabla__num">{version.ventanaOfertaSeg} s</td>
                <td className="tabla__num">{version.radioBusquedaM} m</td>
                <td className="tabla__num">{version.etaMaxSeg} s</td>
                <td className="tabla__num">{version.maxRondasOferta}</td>
                <td className="tabla__num">{version.frescuraPosicionMaxSeg} s</td>
                {/*
                  `editadoPor`/`motivo` pueden ser `null` (columnas nullables: la fila del seed no
                  tiene autor). NO se rellenan con el centinela `SEED` del contrato: la columna no
                  distingue "nació con el sistema" de "alguien la escribió por fuera y no quedó
                  registrado", y ponerlo inventaría una atribución sobre una versión que decide
                  cuánto trabaja cada conductor.
                */}
                <td>{version.editadoPor ?? <span className="apagado">sin registrar</span>}</td>
                <td style={{ fontFamily: 'var(--sans)', maxWidth: 340 }}>
                  {version.motivo ?? <span className="apagado">sin motivo registrado</span>}
                </td>
              </tr>
            ))}
          </Tabla>
        )}
      </Bloque>

      {/*
        CA-05.9 / `02` §9.2bis — el instrumento que dice si `nOfertaSimultanea` está cumpliendo su
        función: la ayuda de ese campo dice "se calibra mirando la tasa de victoria" — esto es esa
        lectura.
      */}
      <Bloque
        titulo="Tasa de victoria del período"
        nota="Ofertas recibidas contra viajes ganados, por conductor — los últimos 7 días. Sólo esto separa un reparto de ofertas parejo de un reparto de VIAJES concentrado."
        sinRelleno
        acciones={
          tasaVictoria.estado === 'error' ? (
            <button type="button" className="boton boton--chico" onClick={recargarTasaVictoria}>
              Reintentar
            </button>
          ) : undefined
        }
      >
        {tasaVictoria.estado === 'cargando' ? (
          <div className="bloque__cuerpo">
            <Cargando filas={4} etiqueta="Cargando la tasa de victoria" />
          </div>
        ) : tasaVictoria.estado === 'error' ? (
          <div className="bloque__cuerpo">
            <ConError detalle={tasaVictoria.mensaje} alReintentar={recargarTasaVictoria} />
          </div>
        ) : (
          (() => {
            const { resumen, porConductor } = tasaVictoria.datos;
            const diagnostico = TEXTO_DIAGNOSTICO[resumen.diagnostico];
            return (
              <>
                <div className="cifras" style={{ padding: '0 16px 16px' }}>
                  <Cifra rotulo="Con ofertas en el período" valor={resumen.conductores} />
                  <Cifra
                    rotulo="Compitieron al menos una vez"
                    valor={resumen.conMuestra}
                    nota={`de ${resumen.conductores}`}
                  />
                  <Cifra
                    rotulo="Mediana de tasa"
                    valor={resumen.medianaTasa === null ? '—' : porcentaje(resumen.medianaTasa)}
                  />
                  <Cifra
                    rotulo="Percentil 10 (la cola de abajo)"
                    valor={resumen.p10Tasa === null ? '—' : porcentaje(resumen.p10Tasa)}
                    tono={resumen.diagnostico === 'REPARTO_CONCENTRADO' ? 'alarma' : undefined}
                  />
                </div>
                <p
                  className="campo__ayuda"
                  style={{ padding: '0 16px 12px', display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <span className={`etiqueta etiqueta--${diagnostico.tono}`}>
                    {resumen.diagnostico}
                  </span>
                  {diagnostico.texto}
                </p>

                {porConductor.length === 0 ? (
                  <div className="bloque__cuerpo">
                    <Vacio
                      titulo="Sin ofertas en este período"
                      detalle="Ningún conductor recibió una oferta en los últimos 7 días."
                    />
                  </div>
                ) : (
                  <Tabla
                    cabeceras={[
                      'Conductor',
                      { texto: 'Ofertas', num: true },
                      { texto: 'Ganadas', num: true },
                      { texto: 'Perdidas', num: true },
                      { texto: 'Expiradas', num: true },
                      { texto: 'Rechazadas', num: true },
                      { texto: 'Tasa de victoria', num: true },
                      '',
                    ]}
                  >
                    {porConductor.map((fila) => {
                      const bajo = fila.tasaVictoria !== null && fila.tasaVictoria < TASA_BAJA;
                      return (
                        <tr
                          key={fila.conductorId}
                          data-estado={
                            bajo ? 'malo' : fila.tasaVictoria === null ? 'atencion' : undefined
                          }
                        >
                          {/*
                            ⚠️ Sin nombre a propósito: el endpoint sólo trae `conductorId` — no
                            existe hoy un listado general de conductores con nombre que cruzar acá
                            (ver la nota de `dominio/dispatchApi.ts`).
                          */}
                          <td className="tenue" style={{ fontFamily: 'var(--mono, monospace)' }}>
                            {fila.conductorId}
                          </td>
                          <td className="tabla__num">{fila.ofertasRecibidas}</td>
                          <td className="tabla__num">{fila.ganadas}</td>
                          <td className="tabla__num">{fila.perdidas}</td>
                          <td className="tabla__num">{fila.expiradas}</td>
                          <td className="tabla__num">{fila.rechazadas}</td>
                          <td className="tabla__num">
                            {fila.tasaVictoria === null ? (
                              <span className="apagado">sin competir</span>
                            ) : (
                              porcentaje(fila.tasaVictoria)
                            )}
                          </td>
                          <td style={{ width: 130 }}>
                            <Barra fraccion={fila.tasaVictoria} bajo={bajo} />
                          </td>
                        </tr>
                      );
                    })}
                  </Tabla>
                )}
              </>
            );
          })()
        )}
      </Bloque>

      {confirmando ? (
        <DialogoMotivo
          titulo={
            sinVersionVigente
              ? `Publicar la versión ${proximaVersion}`
              : `Guardar la versión ${proximaVersion}`
          }
          descripcion={
            // Misma distinción que el botón (ver `calcularEstadoPublicacion`), y acá importa más:
            // el diálogo es el último punto donde el operador puede entender qué va a pasar.
            // Sin versión vigente el reparto YA está corriendo con estos valores de arranque, así
            // que decir "esto cambia cómo se reparten los viajes" seria falso — lo que cambia es
            // que pasa a estar gobernado por una versión y los viajes quedan atribuidos a ella.
            sinVersionVigente
              ? 'Hoy el reparto no lo gobierna ninguna versión: corre con los valores de arranque del sistema. Publicar los deja guardados como la versión 1, y desde ahí cada viaje queda atribuido a ella. Escribí por qué son estos valores: es lo que se va a leer al revisar los números de la semana. Mínimo 10 caracteres — lo exige el backend.'
              : 'Esto cambia cómo se reparten los viajes desde el momento en que se guarda. Escribí qué esperás que pase: es lo que se va a leer al revisar los números de la semana. Mínimo 10 caracteres — lo exige el backend.'
          }
          etiquetaConfirmar={sinVersionVigente ? 'Publicar versión' : 'Guardar versión'}
          alConfirmar={(motivo) => void publicar(motivo)}
          alCancelar={() => setConfirmando(false)}
        />
      ) : null}
    </Pagina>
  );
}
